import { useMemo, useCallback, useState } from 'react';
import { FormLabel } from '@Components/Common/FormComponent';
import {
  Text,
  Flex,
  Input,
  Stack,
  Button,
  Divider,
  SearchInput,
  Icon,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import {
  DataGrid,
  DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
} from '@tonic-one/react-data-grid';
import { useTranslation } from 'react-i18next';
import {
  CorrelatedExpressionCondition,
  ConditionDefinition,
  ConditionOperator,
  isConditionListStringFormat,
  isConditionStringFormat,
  isConditionEnumFormat,
  isConditionIntFormat,
  ConditionDataType,
  ConditionIntFormat,
  ConditionStringFormat,
  ConditionListStringFormat,
} from '../types';
import Dropdown from '@Components/Common/Dropdown';
import IconButton from '@Components/IconButton';
import { AddIcon, CloseSIcon, DeleteIcon } from '@tonic-one/react-icons';
import { toLowerCaseExceptAbbreviation } from '../utils';

const ConditionEditor = (props: {
  condition: CorrelatedExpressionCondition;
  conditionDefinitionsMap: Record<string, ConditionDefinition | undefined>;
  onChange: (condition: CorrelatedExpressionCondition) => void;
  isEmailLoop?: boolean;
}) => {
  const { condition, conditionDefinitionsMap, onChange, isEmailLoop } = props;
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  const [newItemValue, setNewItemValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [rowSelection, setRowSelection] = useState<{ [key: string]: boolean }>(
    {}
  );

  const conditionOptions = useMemo(() => {
    return Object.keys(conditionDefinitionsMap);
  }, [conditionDefinitionsMap]);

  // Matching between CorrelatedExpressionCondition and ConditionDefinition
  const selectedCondition = useMemo(() => {
    if (condition.id === '') return null;
    return conditionDefinitionsMap[condition.id];
  }, [condition.id, conditionDefinitionsMap]);

  const handleConditionChange = useCallback(
    (conditionId: string) => {
      const condDef = conditionDefinitionsMap[conditionId];
      if (!condDef) return;
      const defaultOperator = condDef.raiCondValidOperators[0];
      const newCondition: CorrelatedExpressionCondition = {
        id: conditionId,
        name: condDef.raiCondName,
        operator: defaultOperator,
        marker: condDef.raiCondMarker,
      } satisfies CorrelatedExpressionCondition;

      // Handle range operators
      if (defaultOperator === ConditionOperator.IntLTEGTE) {
        onChange({
          ...newCondition,
          greaterOrEqualValue: 0, // TODO: Use the default value from the format once it's available
          lessOrEqualValue: 0, // TODO: Use the default value from the format once it's available
        });
      } else if (
        isConditionListStringFormat(
          condDef.raiCondFormat,
          condDef.raiCondDataType
        )
      ) {
        onChange({
          ...newCondition,
          valueStrings: condDef.raiCondFormat.defaultValue ?? [],
        });
      } else if (
        isConditionStringFormat(condDef.raiCondFormat, condDef.raiCondDataType)
      ) {
        onChange({
          ...newCondition,
          valueString: condDef.raiCondFormat.defaultValue ?? '',
        });
      } else if (
        isConditionEnumFormat(condDef.raiCondFormat, condDef.raiCondDataType)
      ) {
        onChange({
          ...newCondition,
          value:
            condDef.raiCondFormat.defaultValue ??
            Object.values(condDef.raiCondFormat.enums)[0],
        });
      } else if (
        isConditionIntFormat(condDef.raiCondFormat, condDef.raiCondDataType)
      ) {
        onChange({
          ...newCondition,
          value:
            condDef.raiCondFormat.defaultValue ??
            condDef.raiCondFormat.minValue ??
            0,
        });
      }
    },
    [conditionDefinitionsMap, onChange]
  );

  const getEnumKeyFromValue = useCallback(
    (value: string | number) => {
      if (
        !selectedCondition ||
        !isConditionEnumFormat(
          selectedCondition.raiCondFormat,
          selectedCondition.raiCondDataType
        )
      ) {
        return;
      }
      const enums = selectedCondition.raiCondFormat.enums;
      if (enums) {
        return Object.keys(enums).find((key) => enums[key] === value);
      }
      return value;
    },
    [selectedCondition]
  );

  const handleIntValueChange = useCallback(
    (value: string) => {
      if (!selectedCondition) return;
      const format = selectedCondition.raiCondFormat as ConditionIntFormat;
      let newValue = parseInt(value);
      if (newValue > format.maxValue) {
        newValue = format.maxValue;
      } else if (newValue < format.minValue) {
        newValue = format.minValue;
      }
      onChange({
        ...condition,
        value: newValue,
      });
    },
    [condition, onChange, selectedCondition]
  );

  const handleEnumValueChange = useCallback(
    (value: string) => {
      onChange({
        ...condition,
        value: parseInt(value),
      });
    },
    [condition, onChange]
  );

  const handleAddItem = useCallback(() => {
    if (!selectedCondition || !newItemValue.trim()) return;

    const format = selectedCondition.raiCondFormat as ConditionListStringFormat;
    const currentItems = condition.valueStrings || [];

    if (currentItems.length >= format.maxEntries) return;

    let trimmedValue = newItemValue.trim();
    if (trimmedValue.length > format.eachEntryMaxLength) {
      trimmedValue = trimmedValue.slice(0, format.eachEntryMaxLength);
    }

    if (!currentItems.includes(trimmedValue)) {
      onChange({
        ...condition,
        valueStrings: [...currentItems, trimmedValue],
      });
    }

    setNewItemValue('');
    setRowSelection({});
  }, [selectedCondition, newItemValue, condition, onChange]);

  const handleDeleteSelectedItems = useCallback(() => {
    const currentItems = condition.valueStrings || [];
    const selectedIndices = Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map((key) => parseInt(key));

    const newItems = currentItems.filter(
      (_, index) => !selectedIndices.includes(index)
    );

    onChange({
      ...condition,
      valueStrings: newItems,
    });

    setRowSelection({});
  }, [condition, onChange, rowSelection]);

  const handleDeleteItem = useCallback(
    (index: number) => {
      const currentItems = condition.valueStrings || [];
      const newItems = currentItems.filter((_, i) => i !== index);

      onChange({
        ...condition,
        valueStrings: newItems,
      });

      setRowSelection({});
    },
    [condition, onChange]
  );

  const handleStringValueChange = useCallback(
    (value: string) => {
      if (!selectedCondition) return;
      const format = selectedCondition.raiCondFormat as ConditionStringFormat;
      if (value.length > format.maxLength) {
        value = value.slice(0, format.maxLength);
      }
      onChange({
        ...condition,
        valueString: value,
      });
    },
    [condition, onChange, selectedCondition]
  );

  const filteredData = useMemo(() => {
    const items = condition.valueStrings || [];
    if (!searchValue) {
      return items.map((item, index) => ({ id: index, value: item }));
    }

    return items
      .map((item, index) => ({ id: index, value: item }))
      .filter((item) =>
        item.value.toLowerCase().includes(searchValue.toLowerCase())
      );
  }, [condition.valueStrings, searchValue]);

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((key) => rowSelection[key]),
    [rowSelection]
  );

  const renderValueInput = useCallback(() => {
    if (!selectedCondition) {
      return <Input type="text" disabled flex="1" />;
    }

    if (
      isConditionEnumFormat(
        selectedCondition.raiCondFormat,
        selectedCondition.raiCondDataType
      )
    ) {
      return (
        <Dropdown
          value={`${condition.value}`}
          flex="1"
          options={Object.values(selectedCondition.raiCondFormat.enums).map(
            String
          )}
          renderOption={(option) => {
            const value =
              selectedCondition.raiCondDataType === ConditionDataType.Int
                ? parseInt(option)
                : option;
            return t(
              `globalSetting.correlatedExpressions.userDefinedModal.conditionEnum.${getEnumKeyFromValue(value)}`,
              { defaultValue: getEnumKeyFromValue(value) }
            );
          }}
          onChange={handleEnumValueChange}
        />
      );
    }

    if (
      isConditionListStringFormat(
        selectedCondition.raiCondFormat,
        selectedCondition.raiCondDataType
      )
    ) {
      return null;
    }

    if (
      isConditionIntFormat(
        selectedCondition.raiCondFormat,
        selectedCondition.raiCondDataType
      )
    ) {
      return (
        <Flex gap="2x" flex="1" alignItems="center">
          <Input
            value={condition.value}
            onChange={(e) => handleIntValueChange(e.target.value)}
            type="number"
            min={selectedCondition.raiCondFormat.minValue}
            max={selectedCondition.raiCondFormat.maxValue}
            step={1}
            disabled={!selectedCondition}
            flex="1"
          />
          {selectedCondition.raiCondUnit && (
            <Text flexShrink={0}>{selectedCondition.raiCondUnit}</Text>
          )}
        </Flex>
      );
    }

    if (
      isConditionStringFormat(
        selectedCondition.raiCondFormat,
        selectedCondition.raiCondDataType
      )
    ) {
      return (
        <Flex gap="2x" flex="1" alignItems="center">
          <Input
            value={condition.value}
            onChange={(e) => handleStringValueChange(e.target.value)}
            type="text"
            minLength={selectedCondition.raiCondFormat.minLength}
            maxLength={selectedCondition.raiCondFormat.maxLength}
            disabled={!selectedCondition}
            flex="1"
          />
          {selectedCondition.raiCondUnit && (
            <Text flexShrink={0}>{selectedCondition.raiCondUnit}</Text>
          )}
        </Flex>
      );
    }

    return null;
  }, [
    selectedCondition,
    condition.value,
    handleIntValueChange,
    handleStringValueChange,
    handleEnumValueChange,
    getEnumKeyFromValue,
    t,
  ]);

  const columns = useMemo(() => {
    const conditionName =
      selectedCondition?.raiCondName ||
      t('globalSetting.correlatedExpressions.userDefinedModal.condition');

    const headerProps = {
      backgroundColor: colorStyle.background.secondary,
    };

    return [
      {
        ...DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
        width: 40,
        minWidth: 40,
        headerProps,
      },
      {
        id: 'value',
        header: conditionName,
        accessorKey: 'value',
        width: 'auto',
        cell: ({ row }: { row: { value: { value: string } } }) => (
          <Text>{row.value.value}</Text>
        ),
        headerProps,
      },
      {
        id: 'actions',
        width: 80,
        cell: ({ row }: { row: { value: { id: number } } }) => (
          <IconButton
            onClick={() => handleDeleteItem(row.value.id)}
            tooltipLabel={t('common.action.label.delete')}
            px="0"
            py="0"
          >
            <Icon as={DeleteIcon} />
          </IconButton>
        ),
        headerProps,
      },
    ];
  }, [selectedCondition, t, handleDeleteItem, colorStyle.background.secondary]);

  const renderListStringEditor = useCallback(() => {
    if (
      !selectedCondition ||
      !isConditionListStringFormat(
        selectedCondition.raiCondFormat,
        selectedCondition.raiCondDataType
      )
    ) {
      return null;
    }

    return (
      <Stack gap="4x">
        <Flex gap="2x">
          {!isEmailLoop && (
            <>
              <Input
                flex={1}
                value={newItemValue}
                onChange={(e) => setNewItemValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddItem();
                  }
                }}
                placeholder={t(
                  'globalSetting.correlatedExpressions.userDefinedModal.listString.placeholder',
                  {
                    condition: toLowerCaseExceptAbbreviation(
                      selectedCondition.raiCondName
                    ),
                  }
                )}
                maxLength={selectedCondition.raiCondFormat.eachEntryMaxLength}
              />
              <Button
                gap="2x"
                alignItems="center"
                onClick={handleAddItem}
                disabled={
                  !newItemValue.trim() ||
                  (condition.valueStrings?.length || 0) >=
                    selectedCondition.raiCondFormat.maxEntries
                }
              >
                <AddIcon />
                {t('common.action.label.add')}
              </Button>
              <Divider orientation="vertical" />
            </>
          )}
          {selectedIds.length > 0 && (
            <>
              <Flex gap="1x">
                <Button
                  variant="secondary"
                  gap="2x"
                  alignItems="center"
                  onClick={handleDeleteSelectedItems}
                >
                  <DeleteIcon />
                  {t('common.action.label.delete')}
                </Button>
                <Flex columnGap="1x" alignItems="center" flexShrink={0}>
                  <IconButton onClick={() => setRowSelection({})}>
                    <CloseSIcon size="4x" />
                  </IconButton>
                  <Text color={colorStyle.color.secondary}>
                    {selectedIds.length} {t('common.label.selected')}
                  </Text>
                </Flex>
              </Flex>
              <Divider orientation="vertical" />
            </>
          )}
          <SearchInput
            width={isEmailLoop ? 'auto' : 265}
            flex={isEmailLoop ? 1 : undefined}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={`${t('log.button.search')} ${toLowerCaseExceptAbbreviation(selectedCondition.raiCondName)}`}
            onClearInput={() => setSearchValue('')}
          />
        </Flex>
        {(condition?.valueStrings?.length ?? 0) > 0 && (
          <Flex height={250}>
            <DataGrid
              data={filteredData}
              columns={columns}
              getRowId={(row) => `${row.id}`}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              disableAutoResetRowSelection
              isColumnResizable={false}
              showPagination={false}
              emptyContent={t('common.noData')}
            />
          </Flex>
        )}
      </Stack>
    );
  }, [
    selectedCondition,
    isEmailLoop,
    newItemValue,
    t,
    handleAddItem,
    condition.valueStrings?.length,
    selectedIds.length,
    handleDeleteSelectedItems,
    colorStyle.color.secondary,
    searchValue,
    filteredData,
    columns,
    rowSelection,
  ]);

  return (
    <Stack gap="4x">
      <Flex width="100%">
        <FormLabel
          label={t(
            'globalSetting.correlatedExpressions.userDefinedModal.condition'
          )}
          required
        />
        <Flex gap="2x" width="100%">
          {isEmailLoop ? (
            <Text height={32} display="flex" alignItems="center">
              {selectedCondition?.raiCondName ?? condition.id}
            </Text>
          ) : (
            <Dropdown
              value={condition.id}
              options={conditionOptions}
              onChange={handleConditionChange}
              renderOption={(option) =>
                conditionDefinitionsMap[option]?.raiCondName ?? option
              }
              placeholder={t(
                'globalSetting.correlatedExpressions.userDefinedModal.selectCondition'
              )}
              width={
                selectedCondition &&
                isConditionListStringFormat(
                  selectedCondition.raiCondFormat,
                  selectedCondition.raiCondDataType
                )
                  ? '100%'
                  : 340
              }
              height={32}
              menuHeight={225}
              placeholderAsValue
            />
          )}
          {isEmailLoop ? (
            <Text
              width={170}
              height={32}
              flexShrink={0}
              display="flex"
              alignItems="center"
            >
              {(() => {
                let baseKey =
                  'globalSetting.correlatedExpressions.userDefinedModal.conditionOperator';
                if (
                  selectedCondition &&
                  isConditionEnumFormat(
                    selectedCondition.raiCondFormat,
                    selectedCondition.raiCondDataType
                  )
                ) {
                  baseKey =
                    'globalSetting.correlatedExpressions.userDefinedModal.conditionOperator.enum';
                }
                return t(`${baseKey}.${condition.operator}`);
              })()}
            </Text>
          ) : (
            <Dropdown
              value={condition.operator}
              options={selectedCondition?.raiCondValidOperators ?? []}
              onChange={(operator) => onChange({ ...condition, operator })}
              renderOption={(option) => {
                let baseKey =
                  'globalSetting.correlatedExpressions.userDefinedModal.conditionOperator';
                if (
                  selectedCondition &&
                  isConditionEnumFormat(
                    selectedCondition.raiCondFormat,
                    selectedCondition.raiCondDataType
                  )
                ) {
                  baseKey =
                    'globalSetting.correlatedExpressions.userDefinedModal.conditionOperator.enum';
                }
                return t(`${baseKey}.${option}`);
              }}
              width={170}
              height={32}
              flexShrink={0}
              disabled={!selectedCondition}
            />
          )}
          {renderValueInput()}
        </Flex>
      </Flex>
      {renderListStringEditor()}
    </Stack>
  );
};

export default ConditionEditor;
